import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import { applications, versionsFor } from "../../data";
import { useToast } from "../../components/Toast";

export default function ClientAppPage() {
  const { pkg = "" } = useParams();
  const navigate = useNavigate();
  const push = useToast();
  const app = applications.find((a) => a.pkg === pkg);
  const defaultHasUpdate = pkg === "ru.corp.docs";
  const defaultInstalled = ["ru.corp.mail", "ru.corp.vcs", "ru.corp.market", "ru.corp.security"].includes(pkg) || defaultHasUpdate;
  const [installed, setInstalled] = useState(() => {
    const stored = localStorage.getItem(`astramarket.installed.${pkg}`);
    return stored === null ? defaultInstalled : stored === "true";
  });
  const [hasUpdate, setHasUpdate] = useState(defaultHasUpdate);

  if (!app) {
    return (
      <div className="p-6 text-center">
        <div className="text-[14px] font-medium text-[var(--color-fg)]">Приложение не найдено</div>
        <button onClick={() => navigate("/client")} className="mt-3 text-[12px] text-[var(--color-brand)] hover:underline">← В каталог</button>
      </div>
    );
  }

  const versions = versionsFor(app.pkg);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* верхний бар */}
      <div className="flex items-center justify-between px-4 pt-2">
        <button
          onClick={() => navigate("/client")}
          className="grid h-9 w-9 place-items-center rounded-full text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-2)]"
          aria-label="Назад"
        >
          <ChevronDown size={20} />
        </button>
        <button
          onClick={async () => {
            const url = window.location.href;
            if (navigator.share) await navigator.share({ title: app.name, url });
            else {
              await navigator.clipboard.writeText(url);
              push({ kind: "success", text: "Ссылка скопирована" });
            }
          }}
          aria-label="Поделиться"
          className="grid h-9 w-9 place-items-center rounded-full text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-2)]"
        >
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* Шапка с иконкой и кнопками */}
      <div className="px-4 pt-1">
        <div className="flex items-start gap-3">
          <span
            className="grid shrink-0 place-items-center rounded-2xl font-semibold text-white"
            style={{ background: app.iconColor, width: 56, height: 56, fontSize: 22 }}
          >
            {app.iconLetter}
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)]">{app.name}</h1>
            <div className="mt-0.5 font-mono text-[11px] text-[var(--color-fg-muted)]">
              {app.pkg} · {app.version}
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              {installed && (
                <span className="rounded-full bg-[var(--color-success-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-success)]">
                  ● Установлено {hasUpdate ? "4.1.2" : app.version}
                </span>
              )}
              {hasUpdate && (
                <span className="rounded-full bg-[var(--color-brand)]/15 px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-brand)]">
                  обновление
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          {hasUpdate ? (
            <>
              <button
                onClick={() => {
                  setHasUpdate(false);
                  localStorage.setItem(`astramarket.installed.${app.pkg}`, "true");
                  push({ kind: "success", text: `${app.name} обновлено до ${app.version}` });
                }}
                className="flex-1 rounded-xl bg-[var(--color-brand)] py-3 text-[14px] font-semibold text-white hover:bg-[var(--color-navy-700)]"
              >
                Обновить · 2.1 MB
              </button>
              <button
                onClick={() => push({ kind: "info", text: `${app.name} открыто` })}
                className="rounded-xl border border-[var(--color-border-strong)] px-4 py-3 text-[14px] font-medium text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]"
              >
                Открыть
              </button>
            </>
          ) : installed ? (
            <button
              onClick={() => push({ kind: "info", text: `${app.name} открыто` })}
              className="flex-1 rounded-xl border border-[var(--color-border-strong)] py-3 text-[14px] font-medium text-[var(--color-fg)] hover:bg-[var(--color-surface-2)]"
            >
              Открыть
            </button>
          ) : (
            <button
              onClick={() => {
                setInstalled(true);
                localStorage.setItem(`astramarket.installed.${app.pkg}`, "true");
                push({ kind: "success", text: `${app.name} установлено` });
              }}
              className="flex-1 rounded-xl bg-[var(--color-brand)] py-3 text-[14px] font-semibold text-white hover:bg-[var(--color-navy-700)]"
            >
              Установить · {app.sizeMb} MB
            </button>
          )}
        </div>

        {/* мини-метрики — реальные поля из каталога, без mock-«847 установок» */}
        <ul className="mt-4 grid grid-cols-3 gap-2 border-y border-[var(--color-border)] py-3 text-center">
          <Mini head={`${app.sizeMb} MB`} sub="Размер" />
          <Mini head={`g.${app.generation}`} sub="Поколение" />
          <Mini head="A 8+" sub="Android" />
        </ul>
      </div>

      {/* Скриншоты */}
      <div className="mt-4">
        <h2 className="px-4 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">Скриншоты</h2>
        <div className="-mx-4 mt-2 flex gap-2 overflow-x-auto px-4 pb-1">
          {["#4f7fdf,#1f3a8a", "#8b5cf6,#5b21b6", "#ec4899,#9d174d", "#10b981,#065f46", "#f59e0b,#92400e"].map((g, i) => (
            <div key={i} className="grid h-44 w-28 shrink-0 place-items-end rounded-xl p-2 text-[10px] text-white/80" style={{ background: `linear-gradient(135deg, ${g})` }}>
              <span className="font-mono">screen_{i + 1}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Что нового */}
      <Section title={`Что нового · ${versions[0].version}`}>
        <p className="text-[13px] leading-relaxed text-[var(--color-fg)]">
          {versions[0].changelog}. Подписи в отчётах ускорены на 40%. Обновлены переводы.
        </p>
      </Section>

      <Section title="История">
        <ul className="space-y-2">
          {versions.slice(1, 4).map((v) => (
            <li key={v.version} className="flex items-start justify-between gap-3 text-[12.5px]">
              <div>
                <span className="font-mono font-medium text-[var(--color-fg)]">{v.version}</span>
                <span className="ml-2 text-[var(--color-fg-muted)]">{v.changelog}</span>
              </div>
              <span className="shrink-0 font-mono text-[11px] text-[var(--color-fg-muted)]">{v.date}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Разработчик и группа">
        <ul className="space-y-1.5 text-[12.5px]">
          <li className="flex justify-between"><span className="text-[var(--color-fg-muted)]">Разработчик</span><span className="font-mono">{app.developer}</span></li>
          <li className="flex justify-between"><span className="text-[var(--color-fg-muted)]">Группа доступа</span><span className="font-mono">{app.visibility}</span></li>
          <li className="flex justify-between"><span className="text-[var(--color-fg-muted)]">Поколение каталога</span><span className="font-mono">{app.generation}</span></li>
          <li className="flex justify-between"><span className="text-[var(--color-fg-muted)]">Подпись</span><span className="font-mono">корпоративная ✓</span></li>
        </ul>
      </Section>

      <div className="h-6" />
    </div>
  );
}

function Mini({ head, sub }: { head: string; sub: string }) {
  return (
    <li>
      <div className="font-mono text-[14px] font-semibold text-[var(--color-fg)]">{head}</div>
      <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{sub}</div>
    </li>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 px-4">
      <h2 className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{title}</h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}
